import BackendService from 'BackendService';
import { useEffect, useMemo, useState } from 'react';
import { fetchExtendedInfo, useExtendedInfo } from '../../utils/ExtendedInfoService';
import {
  contactCache,
  fetchActivityLogContactIfNeeded,
  getActivityLogDisplayName,
  isActivityLogContentDiffEvent,
  isActivityLogEmailChangeEvent,
} from './activityLogPageEntryHelpers';

export function useActivityLogPageEntryData(
  log: any,
  expanded: boolean,
  allLogs: any[] = [],
  providedBackendService?: BackendService,
) {
  const [emailSentLog, setEmailSentLog] = useState<any>(null);
  const [loadingEmailSent, setLoadingEmailSent] = useState(false);
  const [contactNames, setContactNames] = useState<Map<string, string>>(new Map());
  const [emailSentExtendedInfo, setEmailSentExtendedInfo] = useState<any>(null);
  const [loadingEmailSentExtendedInfo, setLoadingEmailSentExtendedInfo] = useState(false);
  const [pushReceiptLoading, setPushReceiptLoading] = useState(false);
  const [pushReceiptResult, setPushReceiptResult] = useState<any>(null);
  const [pushReceiptError, setPushReceiptError] = useState<string | null>(null);

  const isContentDiffEvent = isActivityLogContentDiffEvent(log.eventType);
  const isEmailEvent =
    log.eventType === 'EMAIL_SENT' ||
    log.eventType === 'EMAIL_SEND_FAILED' ||
    log.eventType === 'EMAIL_OPENED' ||
    log.eventType === 'EMAIL_CLICKED';
  const isEmailChangeEvent = isActivityLogEmailChangeEvent(log.eventType);
  const isUserProfileUpdated = log.eventType === 'USER_PROFILE_UPDATED';
  const isEmailOpenedOrClicked =
    log.eventType === 'EMAIL_OPENED' || log.eventType === 'EMAIL_CLICKED';
  const isPushNotificationEvent =
    log.eventType === 'PUSH_NOTIFICATION_SENT' ||
    log.eventType === 'PUSH_NOTIFICATION_DELIVERED' ||
    log.eventType === 'PUSH_NOTIFICATION_OPENED' ||
    log.eventType === 'PUSH_NOTIFICATION_DISMISSED';

  const backendService = useMemo(
    () => providedBackendService || new BackendService(),
    [providedBackendService],
  );

  const {
    extendedInfo: fetchedExtendedInfo,
    loading: extendedInfoLoading,
    error: extendedInfoError,
  } = useExtendedInfo(
    log.extendedInfoPath && (expanded || !isContentDiffEvent) ? log.extendedInfoPath : undefined,
    backendService,
  );

  useEffect(() => {
    if (
      isEmailOpenedOrClicked &&
      expanded &&
      fetchedExtendedInfo?.originalEmailLogId &&
      !emailSentLog &&
      !loadingEmailSent
    ) {
      const originalEmailLogId = fetchedExtendedInfo.originalEmailLogId;
      const foundEmailSent = allLogs.find(
        (l: any) =>
          (l.id === originalEmailLogId || l.externalId === originalEmailLogId) &&
          l.eventType === 'EMAIL_SENT',
      );

      if (foundEmailSent) {
        setEmailSentLog(foundEmailSent);
      } else {
        setLoadingEmailSent(true);
        backendService
          .getActivityLogEntry(originalEmailLogId)
          .then((loadedLog) => setEmailSentLog(loadedLog))
          .catch((error) => console.error('Failed to load EMAIL_SENT log:', error))
          .finally(() => setLoadingEmailSent(false));
      }
    }
  }, [
    isEmailOpenedOrClicked,
    expanded,
    fetchedExtendedInfo?.originalEmailLogId,
    emailSentLog,
    loadingEmailSent,
    allLogs,
    backendService,
  ]);

  useEffect(() => {
    if (
      emailSentLog &&
      isEmailOpenedOrClicked &&
      !emailSentExtendedInfo &&
      !loadingEmailSentExtendedInfo
    ) {
      setLoadingEmailSentExtendedInfo(true);
      const extendedInfoPath = emailSentLog.extendedInfoPath || emailSentLog.log?.extendedInfoPath;
      if (extendedInfoPath) {
        fetchExtendedInfo(extendedInfoPath, backendService)
          .then((info) => setEmailSentExtendedInfo(info))
          .catch((error) => console.error('Failed to load EMAIL_SENT extended info:', error))
          .finally(() => setLoadingEmailSentExtendedInfo(false));
      } else {
        setEmailSentExtendedInfo(emailSentLog.extendedInfo || emailSentLog.log?.extendedInfo);
        setLoadingEmailSentExtendedInfo(false);
      }
    }
  }, [
    emailSentLog,
    isEmailOpenedOrClicked,
    emailSentExtendedInfo,
    loadingEmailSentExtendedInfo,
    backendService,
  ]);

  useEffect(() => {
    const resolveContactName = (id: string, name: string) => {
      setContactNames((prev) => new Map(prev).set(id, name));
    };

    const maybeFetchContact = (modelType: string, externalId: string, description: any) => {
      const cached =
        modelType === 'CONTACT' && externalId ? contactCache.get(externalId) : undefined;
      if (!cached?.name && !cached?.loading) {
        const displayName = getActivityLogDisplayName(
          description,
          contactNames,
          modelType,
          externalId,
        );
        if (displayName === 'Unknown') {
          fetchActivityLogContactIfNeeded(
            backendService,
            modelType,
            externalId,
            resolveContactName,
          );
        }
      }
    };

    if (log.initiatedByType === 'CONTACT' && log.initiatedByExternalId) {
      maybeFetchContact(log.initiatedByType, log.initiatedByExternalId, log.initiatedByDescription);
    }

    const onBehalfOfType = log.onBehalfOfType || log.initiatedByDescription?.onBehalfOfType;
    const onBehalfOfExternalId =
      log.onBehalfOfExternalId || log.initiatedByDescription?.onBehalfOfExternalId;
    if (onBehalfOfType === 'CONTACT' && onBehalfOfExternalId) {
      maybeFetchContact(onBehalfOfType, onBehalfOfExternalId, {
        name: log.initiatedByDescription?.onBehalfOfName,
      });
    }

    if (log.associatedModelType === 'CONTACT' && log.associatedModelExternalId) {
      maybeFetchContact(
        log.associatedModelType,
        log.associatedModelExternalId,
        log.associatedModelDescription,
      );
    }

    const advisingForType = log.advisingForType || log.initiatedByDescription?.advisingForType;
    const advisingForExternalId =
      log.advisingForExternalId || log.initiatedByDescription?.advisingForExternalId;
    if (advisingForType === 'CONTACT' && advisingForExternalId) {
      maybeFetchContact(advisingForType, advisingForExternalId, {
        name: log.initiatedByDescription?.advisingForName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    log.initiatedByType,
    log.initiatedByExternalId,
    log.initiatedByDescription,
    log.onBehalfOfType,
    log.onBehalfOfExternalId,
    log.associatedModelType,
    log.associatedModelExternalId,
    log.associatedModelDescription,
    log.advisingForType,
    log.advisingForExternalId,
    backendService,
  ]);

  return {
    backendService,
    contactNames,
    emailSentExtendedInfo,
    emailSentLog,
    extendedInfoError,
    extendedInfoLoading,
    fetchedExtendedInfo,
    isContentDiffEvent,
    isEmailChangeEvent,
    isEmailEvent,
    isEmailOpenedOrClicked,
    isPushNotificationEvent,
    isUserProfileUpdated,
    loadingEmailSent,
    loadingEmailSentExtendedInfo,
    pushReceiptError,
    pushReceiptLoading,
    pushReceiptResult,
    setPushReceiptError,
    setPushReceiptLoading,
    setPushReceiptResult,
  };
}
